/**
 * MenageriePanel
 *
 * Sprite gallery for HD-2D iso sprites. Every entity (character or mob
 * instance) has its own UUID-keyed 4-facing sprite set. Shows all generated
 * sprite bundles with per-facing and per-entity regeneration controls.
 */

import { useEffect, useMemo, useState } from 'react';
import { useIsoSpriteStore, ALL_FACINGS, type SpriteFacing, type SpriteGenParams } from '../../stores/isoSpriteStore';
import { useCharacterStore } from '../../stores/characterStore';
import { useMobStore } from '../../stores/mobStore';
import { buildMobSpriteParams } from '../../lib/mobSpritePrompt';
import { trpc } from '../../lib/trpc';
import { Icon } from '../Icon';

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.4)';

const FACING_LABELS: Record<SpriteFacing, string> = {
  south: 'Front',
  north: 'Back',
  east: 'Right',
  west: 'Left',
};

const FACING_ARROWS: Record<SpriteFacing, string> = {
  south: '\u2193',
  north: '\u2191',
  east: '\u2192',
  west: '\u2190',
};

function downloadSprite(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function downloadBundle(id: string, label: string, sprites: Record<string, string>) {
  const slug = label.toLowerCase().replace(/\s+/g, '-');
  for (const facing of ALL_FACINGS) {
    const url = sprites[`${id}:${facing}`];
    if (url) downloadSprite(url, `${slug}-${facing}.png`);
  }
}

interface EntityRow {
  id: string;
  label: string;
  subtitle: string;
  kind: 'character' | 'mob' | 'orphan';
  /** Persistence target — which DB row to save sprites to */
  persistTarget: { kind: 'character'; characterId: string }
    | { kind: 'bestiary'; bestiaryId: string }
    | { kind: 'mob'; mobId: string }
    | null;
  resolveParams: () => SpriteGenParams;
}

export function MenageriePanel() {
  useEffect(() => {
    useIsoSpriteStore.getState().loadFromCache();
  }, []);

  const sprites = useIsoSpriteStore((s) => s.sprites);
  const generating = useIsoSpriteStore((s) => s.generating);
  const seeds = useIsoSpriteStore((s) => s.seeds);
  const storedNames = useIsoSpriteStore((s) => s.names);
  const characters = useCharacterStore((s) => s.characters);
  const mobs = useMobStore((s) => s.mobs);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [saveError, setSaveError] = useState<string | null>(null);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);

  // One-click: sync every entity's cached sprites to DB. Iterates rows with
  // a persistTarget (skips orphans), uploads 4 facings each, records saved state.
  const handleBulkSync = async () => {
    const rowsWithTarget = rows.filter((r) => r.persistTarget && !savedIds.has(r.id));
    if (rowsWithTarget.length === 0) return;
    setBulkSyncing(true);
    setSaveError(null);
    setBulkProgress({ done: 0, total: rowsWithTarget.length });

    for (let i = 0; i < rowsWithTarget.length; i++) {
      const row = rowsWithTarget[i];
      try {
        await handlePersist(row, { silent: true });
      } catch (err) {
        console.warn(`[Menagerie] Bulk sync failed for ${row.label}:`, err);
      }
      setBulkProgress({ done: i + 1, total: rowsWithTarget.length });
    }

    setBulkSyncing(false);
    setBulkProgress(null);
  };

  // Persist sprites to DB — uploads 4 base64 facings, writes URLs to the
  // owning row (bestiary template or character `data` JSONB).
  const handlePersist = async (row: EntityRow, opts: { silent?: boolean } = {}) => {
    if (!row.persistTarget) return;
    if (!opts.silent) setSavingId(row.id);
    if (!opts.silent) setSaveError(null);
    try {
      const facings: Partial<Record<SpriteFacing, string>> = {};
      for (const f of ALL_FACINGS) {
        const url = sprites[`${row.id}:${f}`];
        if (url) facings[f] = url;
      }
      if (Object.keys(facings).length === 0) {
        if (!opts.silent) setSaveError('Nothing to save — generate sprites first');
        if (!opts.silent) setSavingId(null);
        return;
      }

      if (row.persistTarget.kind === 'character') {
        await trpc.sprites.saveToCharacter.mutate({
          characterId: row.persistTarget.characterId,
          sprites: facings,
        });
      } else if (row.persistTarget.kind === 'bestiary') {
        await trpc.sprites.saveToBestiary.mutate({
          bestiaryId: row.persistTarget.bestiaryId,
          sprites: facings,
        });
      } else {
        await trpc.sprites.saveToMob.mutate({
          mobId: row.persistTarget.mobId,
          sprites: facings,
        });
      }

      setSavedIds((s) => new Set(s).add(row.id));
    } catch (err) {
      if (!opts.silent) setSaveError((err as Error).message ?? 'Save failed');
      throw err;
    } finally {
      if (!opts.silent) setSavingId(null);
    }
  };

  const rows: EntityRow[] = useMemo(() => {
    const idsWithSprites = new Set<string>();
    for (const key of Object.keys(sprites)) {
      const id = key.split(':')[0];
      if (id) idsWithSprites.add(id);
    }

    const result: EntityRow[] = [];
    for (const id of idsWithSprites) {
      const char = characters.get(id);
      if (char) {
        result.push({
          id,
          label: char.name,
          subtitle: `${char.race || ''} ${char.classes?.[0]?.name || ''}`.trim() || 'Character',
          kind: 'character',
          persistTarget: { kind: 'character', characterId: char.id },
          resolveParams: () => {
            const mainClass = char.classes?.[0];
            return {
              id: char.id,
              name: char.name,
              race: char.race,
              className: mainClass?.name,
              subclass: mainClass?.subclass,
              equipment: char.equipment,
              personality: char.personalityTraits,
              appearance: char.appearance,
              isNpc: char.isNpc,
            };
          },
        });
        continue;
      }

      const mob = mobs.get(id);
      if (mob) {
        // Prefer bestiary template (shared across instances of this type), but
        // fall back to per-instance mob-row persistence for ad-hoc spawns.
        const bestiaryId = (mob as unknown as { bestiaryId?: string | null }).bestiaryId ?? null;
        const persistTarget = bestiaryId
          ? { kind: 'bestiary' as const, bestiaryId }
          : { kind: 'mob' as const, mobId: mob.id };
        result.push({
          id,
          label: mob.displayName || mob.name,
          subtitle: `${mob.type || 'creature'} · ${mob.size || 'medium'}`,
          kind: 'mob',
          persistTarget,
          resolveParams: () => buildMobSpriteParams(mob),
        });
        continue;
      }

      // Orphan — sprite exists but entity is gone. Keep it viewable.
      const fallbackName = storedNames[id] ?? `Entity ${id.substring(0, 8)}`;
      result.push({
        id,
        label: fallbackName,
        subtitle: 'Orphaned sprite',
        kind: 'orphan',
        persistTarget: null,
        resolveParams: () => ({ id, name: fallbackName }),
      });
    }

    // Characters first, then mobs, then orphans — label alphabetical within group.
    return result.sort((a, b) => {
      const kindOrder = { character: 0, mob: 1, orphan: 2 } as const;
      if (kindOrder[a.kind] !== kindOrder[b.kind]) return kindOrder[a.kind] - kindOrder[b.kind];
      return a.label.localeCompare(b.label);
    });
  }, [sprites, characters, mobs, storedNames]);

  if (rows.length === 0) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 12, padding: 32, minHeight: 200,
      }}>
        <Icon name="users" size={32} color={GOLD_DIM} />
        <div style={{ color: GOLD_DIM, fontSize: 13, fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
          No sprites generated yet
        </div>
        <div style={{ color: 'rgba(192,192,192,0.4)', fontSize: 11, fontFamily: 'Georgia, serif', textAlign: 'center' }}>
          Switch to Isometric View to generate HD-2D sprites.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '0 4px', marginBottom: 4,
      }}>
        <span style={{ fontFamily: 'Georgia, serif', fontSize: 11, color: 'rgba(192,192,192,0.5)' }}>
          {rows.length} entit{rows.length !== 1 ? 'ies' : 'y'} &middot;{' '}
          {Object.keys(sprites).length} sprites
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          {/* Bulk DB sync — uploads every entity's sprites to Storage + DB in one pass */}
          <button
            onClick={handleBulkSync}
            disabled={bulkSyncing || rows.filter((r) => r.persistTarget && !savedIds.has(r.id)).length === 0}
            title="Upload every entity's sprites to DB so other players see them"
            style={{
              background: bulkSyncing ? 'rgba(212,175,55,0.15)' : GOLD,
              border: 'none', borderRadius: 4,
              color: bulkSyncing ? 'rgba(192,192,192,0.5)' : '#1a0f0a',
              cursor: bulkSyncing ? 'wait' : 'pointer',
              fontFamily: 'Georgia, serif', fontSize: 10, fontWeight: 600,
              padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <Icon name={bulkSyncing ? 'loader' : 'save'} size={11} color={bulkSyncing ? 'rgba(192,192,192,0.5)' : '#1a0f0a'} />
            {bulkSyncing && bulkProgress
              ? `Syncing ${bulkProgress.done}/${bulkProgress.total}…`
              : 'Sync All to DB'}
          </button>
          <button
            onClick={() => {
              for (const r of rows) downloadBundle(r.id, r.label, sprites);
            }}
            title="Download all sprites as PNGs"
            style={{
              background: 'none', border: `1px solid ${GOLD_DIM}`, borderRadius: 4,
              color: GOLD, cursor: 'pointer', fontFamily: 'Georgia, serif', fontSize: 10,
              padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <Icon name="save" size={11} color={GOLD} />
            Download
          </button>
        </div>
      </div>

      {rows.map((row) => {
        const hasSeed = !!seeds[row.id];
        const facingCount = ALL_FACINGS.filter((f) => !!sprites[`${row.id}:${f}`]).length;
        const isExpanded = expandedId === row.id;
        const isGen = ALL_FACINGS.some((f) => generating.has(`${row.id}:${f}`));

        return (
          <div
            key={row.id}
            style={{
              backgroundColor: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 6,
              overflow: 'hidden',
            }}
          >
            <button
              onClick={() => setExpandedId(isExpanded ? null : row.id)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 10px', border: 'none', background: 'none', cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{
                width: 36, height: 48, borderRadius: 4, overflow: 'hidden',
                backgroundColor: 'rgba(0,0,0,0.3)', flexShrink: 0,
              }}>
                {sprites[`${row.id}:south`] && (
                  <img
                    src={sprites[`${row.id}:south`]}
                    alt={row.label}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: 'Georgia, serif', fontSize: 12, color: '#f4e4bc',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {row.label}
                </div>
                <div style={{
                  fontFamily: 'Georgia, serif', fontSize: 10,
                  color: 'rgba(192,192,192,0.5)', marginTop: 1,
                }}>
                  {row.subtitle}
                  {hasSeed ? ' \u2022 seed' : ''}
                </div>
              </div>

              <span style={{
                fontSize: 10, fontFamily: 'Georgia, serif', padding: '2px 6px',
                borderRadius: 3, flexShrink: 0,
                backgroundColor: facingCount >= 4
                  ? 'rgba(76,175,80,0.15)'
                  : isGen ? 'rgba(212,175,55,0.15)' : 'rgba(255,255,255,0.05)',
                color: facingCount >= 4 ? '#4caf50' : isGen ? GOLD : 'rgba(192,192,192,0.5)',
              }}>
                {isGen ? '...' : `${facingCount}/4`}
              </span>

              <Icon
                name={isExpanded ? 'chevron-up' : 'chevron-down'}
                size={12}
                color="rgba(192,192,192,0.4)"
              />
            </button>

            {isExpanded && (
              <div style={{ padding: '4px 10px 10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6,
                  marginBottom: 8,
                }}>
                  {ALL_FACINGS.map((facing) => {
                    const key = `${row.id}:${facing}`;
                    const url = sprites[key];
                    const isFacingGen = generating.has(key);

                    return (
                      <div key={facing} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        <div style={{
                          width: '100%', aspectRatio: '2/3', borderRadius: 4,
                          overflow: 'hidden', backgroundColor: 'rgba(0,0,0,0.3)',
                          border: isFacingGen ? `1px solid ${GOLD_DIM}` : '1px solid rgba(255,255,255,0.06)',
                          position: 'relative',
                        }}>
                          {url ? (
                            <img
                              src={url}
                              alt={`${row.label} ${facing}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <div style={{
                              width: '100%', height: '100%', display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                              color: 'rgba(192,192,192,0.2)', fontSize: 18,
                            }}>
                              {isFacingGen ? <Icon name="loader" size={16} color={GOLD_DIM} /> : '?'}
                            </div>
                          )}
                        </div>

                        <span style={{
                          fontSize: 9, fontFamily: 'Georgia, serif',
                          color: 'rgba(192,192,192,0.5)',
                        }}>
                          {FACING_ARROWS[facing]} {FACING_LABELS[facing]}
                        </span>

                        <div style={{ display: 'flex', gap: 2 }}>
                          <button
                            title={`Re-generate ${FACING_LABELS[facing]}`}
                            disabled={isFacingGen}
                            onClick={() => regenFacing(row.resolveParams(), facing)}
                            style={{
                              background: 'none', border: '1px solid rgba(255,255,255,0.1)',
                              borderRadius: 3, padding: '2px 4px', cursor: isFacingGen ? 'wait' : 'pointer',
                              color: 'rgba(192,192,192,0.5)', fontSize: 9,
                            }}
                          >
                            <Icon name="refresh-cw" size={9} color="rgba(192,192,192,0.5)" />
                          </button>

                          {url && (
                            <button
                              title={`Download ${FACING_LABELS[facing]}`}
                              onClick={() => downloadSprite(url, `${row.label.toLowerCase().replace(/\s+/g, '-')}-${facing}.png`)}
                              style={{
                                background: 'none', border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: 3, padding: '2px 4px', cursor: 'pointer',
                                color: 'rgba(192,192,192,0.5)', fontSize: 9,
                              }}
                            >
                              <Icon name="save" size={9} color="rgba(192,192,192,0.5)" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => downloadBundle(row.id, row.label, sprites)}
                    style={actionBtnStyle}
                  >
                    <Icon name="save" size={11} color={GOLD} />
                    Download Bundle
                  </button>

                  <button
                    disabled={isGen}
                    onClick={() => regenAll(row.resolveParams())}
                    style={{ ...actionBtnStyle, opacity: isGen ? 0.5 : 1 }}
                  >
                    <Icon name="refresh-cw" size={11} color={GOLD} />
                    {isGen ? 'Generating...' : 'Re-generate All'}
                  </button>

                  {sprites[`${row.id}:south`] && !hasSeed && (
                    <button
                      onClick={() => useIsoSpriteStore.getState().promoteFacingToSeed(row.id, 'south')}
                      style={actionBtnStyle}
                    >
                      <Icon name="star" size={11} color={GOLD} />
                      Set as Seed
                    </button>
                  )}

                  {/* Persist to DB (bestiary template / character row) */}
                  {row.persistTarget && (
                    <button
                      disabled={savingId === row.id}
                      onClick={() => handlePersist(row)}
                      title={row.persistTarget.kind === 'bestiary'
                        ? 'Save sprites to bestiary template — future spawns inherit them'
                        : row.persistTarget.kind === 'mob'
                          ? 'Save sprites to this mob instance — persists across devices'
                          : 'Save sprites to character — persists across devices'}
                      style={{
                        ...actionBtnStyle,
                        opacity: savingId === row.id ? 0.6 : 1,
                        borderColor: savedIds.has(row.id) ? '#6fbd6f' : GOLD_DIM,
                        color: savedIds.has(row.id) ? '#6fbd6f' : GOLD,
                      }}
                    >
                      <Icon
                        name={savingId === row.id ? 'loader' : savedIds.has(row.id) ? 'check' : 'save'}
                        size={11}
                        color={savedIds.has(row.id) ? '#6fbd6f' : GOLD}
                      />
                      {savingId === row.id
                        ? 'Saving…'
                        : savedIds.has(row.id)
                          ? 'Saved'
                          : row.persistTarget.kind === 'bestiary'
                            ? 'Save to Bestiary'
                            : row.persistTarget.kind === 'mob'
                              ? 'Save to Mob'
                              : 'Save to Character'}
                    </button>
                  )}
                </div>

                {saveError && expandedId === row.id && (
                  <div style={{ fontSize: 10, color: '#d85050', marginTop: 4 }}>
                    {saveError}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const actionBtnStyle: React.CSSProperties = {
  background: 'none',
  border: `1px solid ${GOLD_DIM}`,
  borderRadius: 4,
  color: GOLD,
  cursor: 'pointer',
  fontFamily: 'Georgia, serif',
  fontSize: 10,
  padding: '4px 8px',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
};

function regenFacing(params: SpriteGenParams, facing: SpriteFacing) {
  useIsoSpriteStore.setState((s) => {
    const newSprites = { ...s.sprites };
    delete newSprites[`${params.id}:${facing}`];
    try { localStorage.setItem('machina:iso-sprites', JSON.stringify(newSprites)); } catch { /* quota */ }
    return { sprites: newSprites };
  });
  useIsoSpriteStore.getState().generateSprite(params, facing);
}

function regenAll(params: SpriteGenParams) {
  useIsoSpriteStore.setState((s) => {
    const newSprites = { ...s.sprites };
    for (const f of ALL_FACINGS) delete newSprites[`${params.id}:${f}`];
    try { localStorage.setItem('machina:iso-sprites', JSON.stringify(newSprites)); } catch { /* quota */ }
    return { sprites: newSprites };
  });
  useIsoSpriteStore.getState().generateMissing([params]);
}
